import { useCallback, useMemo, useState } from "react";

import * as ModelConverter from "@/components/features/_model/converter";

import * as DiscountItemMultiShared from "../../_shared";

export const useEditDiscountMultiFormModel = (
	initInputModel: DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["InputModel"],
) => {
	/**
	 * DefaultInputModel の初期化
	 */
	const defaultInputModel = useMemo(
		() => DiscountItemMultiShared.Model.Factory.Default.discountMultiInputModelFactory(initInputModel.hotelID),
		[initInputModel.hotelID],
	);

	/**
	 * CurrentInputModel の初期化
	 */
	const [currentInputModel, setCurrentInputModel] =
		useState<DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["InputModel"]>(initInputModel);

	/**
	 * ViewModel の初期化
	 */
	const [viewModel, setViewModel] = useState<
		DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["ViewModel"]
	>(
		ModelConverter.inputModelToViewModelConverter<
			DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["InputModel"],
			DiscountItemMultiShared.Model.ModelType.ExcludeKeys,
			DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["QueryData"],
			DiscountItemMultiShared.Model.ValidationSchema.DiscountMultiInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: initInputModel,
			currentInput: initInputModel,
			excludeKeys: DiscountItemMultiShared.Model.ModelType.excludeKeys,
			zodSchema: DiscountItemMultiShared.Model.ValidationSchema.DiscountMultiInputZodSchema,
		}),
	);

	/**
	 * ViewModel を更新する関数。
	 * この関数は useActions でのみ利用される。
	 * @see file://./use-case/use-actions.ts
	 *
	 * DefaultInputModel は ViewModel と比較し、各 Node の変更差分時に利用される。
	 * InitInputModel は ViewModel と lodash.isequal での全体比較時に利用される。
	 * CurrentInputModel は 変更された ViewModel の値を元にバリデーションを実行する際に利用される。
	 * その為、一度 ViewModel -> InputModel に変換し、再度 InputModel -> ViewModel に変換する。
	 */
	const onSetViewModel = useCallback(
		(_viewModel: DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["ViewModel"]) => {
			const convertedInputModel = ModelConverter.viewModelToInputModelConverter(_viewModel);
			const convertedViewModel = ModelConverter.inputModelToViewModelConverter<
				DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["InputModel"],
				DiscountItemMultiShared.Model.ModelType.ExcludeKeys,
				DiscountItemMultiShared.Model.ModelType.DiscountMultiFormModel["QueryData"],
				DiscountItemMultiShared.Model.ValidationSchema.DiscountMultiInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: initInputModel,
				currentInput: convertedInputModel,
				excludeKeys: DiscountItemMultiShared.Model.ModelType.excludeKeys,
				zodSchema: DiscountItemMultiShared.Model.ValidationSchema.DiscountMultiInputZodSchema,
			});
			// state の更新
			setCurrentInputModel(convertedInputModel);
			setViewModel(convertedViewModel);
		},
		[defaultInputModel, initInputModel],
	);

	const onResetViewModel = useCallback(() => {
		setViewModel({
			...viewModel,
			isDirty: false,
		});
	}, [viewModel]);

	return {
		inputModel: currentInputModel,
		viewModel,

		onSetViewModel,
		onResetViewModel,
	};
};
